import { string } from 'fp-ts';
import { Reservation, Service } from '@mews-commander/data-access';
import { DateTimePart, DateTimeUtcService, TimeUnitUtils } from '@mews/framework';
import {
    isCanceledState,
    isInquiredState,
    isProcessedState,
    isRequestedState,
    isStartedState,
} from '@mews-commander/types';
import { isBookableService } from '../ducks/servicesNew';

export const getReservationPersonCount = ({ occupancyData }: Reservation) =>
    occupancyData ? occupancyData.reduce((acc, { personCount }) => acc + personCount, 0) : 0;

export const isReservationLockable = ({ assignedResourceId, serviceOrderState }: Reservation) =>
    assignedResourceId !== null &&
    !isInquiredState(serviceOrderState) &&
    !isCanceledState(serviceOrderState) &&
    !isRequestedState(serviceOrderState);

export const isReservationCancellable = ({ serviceOrderState }: Reservation): boolean =>
    !isStartedState(serviceOrderState) && !isCanceledState(serviceOrderState) && !isProcessedState(serviceOrderState);

export const isReservationCanceled = (reservation: Reservation) => isCanceledState(reservation.serviceOrderState);

export const compareEndUtcAsc = (a: Reservation, b: Reservation) => string.Ord.compare(a.endUtc, b.endUtc);

export const compareEndUtcDesc = (a: Reservation, b: Reservation) => string.Ord.compare(b.endUtc, a.endUtc);

export const getReservationLatestUndoCancelationUtc = (
    { canceledUtc, endUtc }: Reservation,
    timezone: string,
    service: Service,
    ehwMilliseconds: number
) => {
    if (!canceledUtc || !isBookableService(service) || !service.data.value) {
        return null;
    }
    const { startOffset, timeUnitPeriod } = service.data.value;

    const timePart = TimeUnitUtils.timeUnitToDateTimePart(timeUnitPeriod);
    const lastTimeUnitStartUtc = TimeUnitUtils.getLastServiceTimeUnitStartUtcFromEndUtc(
        endUtc,
        timezone,
        startOffset,
        timePart
    );

    const addMillis = (utc: string, millisCount: number) =>
        DateTimeUtcService.toISOString(
            DateTimeUtcService.add(DateTimePart.Milliseconds)(DateTimeUtcService.parse(utc), millisCount)
        );

    const modifiedEnd = addMillis(lastTimeUnitStartUtc, ehwMilliseconds);
    const modifiedCanceled = addMillis(canceledUtc, ehwMilliseconds);

    return modifiedEnd < modifiedCanceled ? modifiedEnd : modifiedCanceled;
};
